import { useMemo } from 'react';

import { useCodeFlavor, type CodeFlavor } from '../../lib/code-flavor-context';
import { useNetwork } from '../../lib/network-context';
import { TerminalCodeBlock, type CodeTab } from './CodeBlock';

interface QuickstartPageProps {
  onPickInstruction?: (name: string) => void;
}

const TS_TABS = (k: string, w: string): CodeTab[] => [
  { id: 'kit', label: '@solana/kit', source: k, lang: 'typescript' },
  { id: 'web3js', label: '@solana/web3.js', source: w, lang: 'typescript' },
];

const SH_TABS = (k: string, w: string): CodeTab[] => [
  { id: 'kit', label: '@solana/kit', source: k, lang: 'bash' },
  { id: 'web3js', label: '@solana/web3.js', source: w, lang: 'bash' },
];

export function QuickstartPage({ onPickInstruction }: QuickstartPageProps) {
  const { rpcUrl } = useNetwork();
  const { flavor, setFlavor } = useCodeFlavor();
  const onTab = (id: string) => setFlavor(id as CodeFlavor);

  const installTabs = useMemo(
    () =>
      SH_TABS(
        `pnpm add @sly-rentals/core @solana/kit`,
        `pnpm add @sly-rentals/core @solana/web3.js`
      ),
    []
  );

  const configureTabs = useMemo(
    () =>
      TS_TABS(
        `import { setSdkConfig } from '@sly-rentals/core';

setSdkConfig({
  network: 'atlasnet',          // 'mainnet-beta' | 'atlasnet' | 'devnet'
  rpcUrl: '${rpcUrl}',
  commitment: 'confirmed',
});`,
        `import { setSdkConfig } from '@sly-rentals/core/legacy';
import { PublicKey } from '@solana/web3.js';

setSdkConfig({
  network: 'atlasnet',
  rpcUrl: '${rpcUrl}',
  commitment: 'confirmed',
  PublicKey,                    // signal web3.js mode to the SDK
});`
      ),
    [rpcUrl]
  );

  const buildTabs = useMemo(
    () =>
      TS_TABS(
        `import { createContract } from '@sly-rentals/core';

const ix = await createContract({
  owner: walletAddress,         // string | TransactionSigner
  fleet: fleetAddress,
  rate: { atlas: 1.5 },
  paymentsFreq: '@daily',
  durationMax: { days: 30 },
});

// Wrap in your own transaction + sign with whatever wallet flow you use.`,
        `import { createContract } from '@sly-rentals/core/legacy';

const instructions = await createContract({
  owner: wallet.publicKey,      // PublicKey | Keypair | string
  fleet: fleetAddress,
  rate: { atlas: 1.5 },
  paymentsFreq: '@daily',
  durationMax: { days: 30 },
});

const tx = new Transaction().add(...instructions);`
      ),
    []
  );

  return (
    <div>
      <div className="kicker">[ 00 / quickstart ]</div>
      <h1 className="page-title">
        Drive the protocol from <em>your own client.</em>
      </h1>
      <p className="page-sub">
        The playground mirrors the SDK call surface. Once you've reproduced an instruction here via{' '}
        <span className="cyan mono">Try-it</span>, the snippet on the right is the same code that'll
        work in your app.
      </p>

      <div className="section-head">
        <h2>1 — Install</h2>
        <p>Pick the flavor that matches your codebase. Both ship the same SDK surface.</p>
      </div>
      <TerminalCodeBlock tabs={installTabs} active={flavor} onActiveChange={onTab} meta="sh" />

      <div className="section-head">
        <h2>2 — Configure</h2>
        <p>
          Set the global SDK config once at app startup. The network bar (top of the page) does this
          for you.
        </p>
      </div>
      <TerminalCodeBlock tabs={configureTabs} active={flavor} onActiveChange={onTab} meta="ts" />

      <div className="section-head">
        <h2>3 — Build an instruction</h2>
        <p>
          Each SDK function returns a transaction-ready instruction. Wallet integration is up to you
          — the SDK never signs.
        </p>
      </div>
      <TerminalCodeBlock tabs={buildTabs} active={flavor} onActiveChange={onTab} meta="ts" />

      <div className="section-head">
        <h2>4 — Try-it</h2>
        <p>
          Pick any instruction. The form auto-builds from the SDK's TypeScript signatures, the
          snippet on the right updates as you type, and the Try-it button signs + submits via your
          connected wallet.
        </p>
      </div>
      {onPickInstruction && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          <button
            type="button"
            className="sly-btn"
            onClick={() => onPickInstruction('createContract')}
          >
            createContract
          </button>
          <button
            type="button"
            className="sly-btn"
            onClick={() => onPickInstruction('acceptRental')}
          >
            acceptRental
          </button>
          <button
            type="button"
            className="sly-btn"
            onClick={() => onPickInstruction('createBorrower')}
          >
            createBorrower
          </button>
        </div>
      )}
    </div>
  );
}
